import { useState } from "react";

import { useFieldArray, useForm } from "react-hook-form";

import { type AirHandlerUnit, type Floor } from "@eisox/api/core-api";
import { ActionButton, Select, TextField } from "@eisox/design-system";
import { ArrowRightIcon } from "@eisox/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBlocker } from "@tanstack/react-router";

import { FieldContainer } from "~/components/field-container";
import { ModificationDialog } from "~/components/modification-dialog";
import { RoomsSelect } from "~/components/rooms-select";
import { compareAirHandlerUnitNames } from "~/features/air-handler-unit";
import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";

import { groupsSchema, type GroupsSchemaType, type Group as GroupType } from "../helpers";
import { AddInput } from "./add-group-input";
import { Group } from "./group";
import { GroupPlanEditor } from "./group-plan";

const NAME = "Groups";

interface GroupsProps {
  buildingId: string;
  floors: Floor[];
  rooms?: EnhancedRoom[];
  airHandlerUnits?: AirHandlerUnit[];
  groups: GroupType[];
  onGroupsChange: (groups: GroupType[]) => Promise<void>;
}

export const Groups: React.FC<GroupsProps> = ({
  buildingId,
  floors,
  rooms,
  airHandlerUnits,
  groups,
  onGroupsChange,
}) => {
  const {
    control,
    formState: { isDirty, errors },
    handleSubmit,
    reset,
  } = useForm<GroupsSchemaType>({
    resolver: zodResolver(groupsSchema()),
    defaultValues: {
      groups: groups,
    },
  });

  const { fields, append, update, remove } = useFieldArray({ control, name: "groups" });

  const { proceed, reset: resetBlocker, status } = useBlocker({ shouldBlockFn: () => isDirty, withResolver: true });

  const [newGroup, setNewGroup] = useState<string>("");
  const [currentGroupIndex, setCurrentGroupIndex] = useState<number | null>(fields.length > 0 ? 0 : null);
  const [itemHovered, setItemHovered] = useState<string | undefined>();

  const handleChangeGroup = (index: number) => {
    setCurrentGroupIndex(index);
  };

  const handleAddGroup = (name: string) => {
    append({ name: name, items: [] });
    setCurrentGroupIndex(fields.length > 0 ? fields.length : 0);
  };

  const handleDeleteGroup = (index: number) => {
    remove(index);
    if (currentGroupIndex === index) setCurrentGroupIndex(null);
  };

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currentGroupIndex !== null && fields[currentGroupIndex]) {
      update(currentGroupIndex, { name: e.target.value, items: fields[currentGroupIndex].items });
    }
  };

  const handleChangeItems = (value: string[]) => {
    if (currentGroupIndex !== null && fields[currentGroupIndex]) {
      update(currentGroupIndex, { name: fields[currentGroupIndex].name, items: value });
    }
  };

  const onSubmit = async (data: GroupsSchemaType) => {
    await onGroupsChange(data.groups);
    reset({ groups: data.groups });
  };

  return (
    <>
      <form
        className="gap-y-5 gap-x-10 grid h-full grid-cols-[204px_auto] grid-rows-[auto_44px] overflow-y-auto"
        onSubmit={e => void handleSubmit(onSubmit)(e)}
      >
        <div className="min-h-125 flex flex-col justify-between">
          <div className="mb-2.5 flex max-h-[calc(100%-170px)] flex-col">
            <h2 className="font-semibold leading-5.25 mb-2.5 text-lg">{m.groups()}</h2>
            <div className="mb-2.5 flex flex-col overflow-y-auto">
              {fields
                .map(g => g.name)
                .map((name, index) => {
                  const groupErrors = errors.groups ? errors.groups[index] : undefined;
                  return (
                    <Group
                      key={index}
                      name={name}
                      onClick={() => {
                        handleChangeGroup(index);
                        setNewGroup("");
                      }}
                      onDelete={() => handleDeleteGroup(index)}
                      selected={index === currentGroupIndex}
                      errors={groupErrors}
                    />
                  );
                })}
            </div>
            <AddInput
              groupNames={fields.map(f => f.name)}
              onAddGroup={name => handleAddGroup(name)}
              value={newGroup}
              handleChangeValue={value => setNewGroup(value)}
              handleFocus={() => setCurrentGroupIndex(null)}
            />
            {errors.groups && <p>{errors.groups.message}</p>}
          </div>
          {currentGroupIndex !== null && fields[currentGroupIndex] && (
            <div className="gap-2.5 flex flex-col">
              <FieldContainer label={m.name()}>
                <TextField.Root
                  placeholder={m.group_name()}
                  value={fields[currentGroupIndex].name}
                  onChange={handleChangeName}
                />
              </FieldContainer>
              {rooms && rooms.length > 0 && (
                <RoomsSelect
                  multiple
                  buildingId={buildingId}
                  floors={floors}
                  value={fields[currentGroupIndex].items}
                  rooms={rooms}
                  handleRoomHovered={value => setItemHovered(value)}
                  onValueChange={(value: string | string[]) => handleChangeItems(value as string[])}
                  side="right"
                  align="end"
                />
              )}
              {airHandlerUnits && airHandlerUnits.length > 0 && (
                <Select.Root
                  multiple
                  value={fields[currentGroupIndex].items}
                  onValueChange={items => handleChangeItems(items)}
                >
                  <Select.Trigger>
                    <Select.Value placeholder={m.select_places({ resource: "airHandlerUnit" })} />
                  </Select.Trigger>
                  <Select.Content side="right" align="end">
                    {airHandlerUnits.length > 10 && <Select.Input placeholder={m.search()} />}
                    <Select.Viewport>
                      {[...airHandlerUnits]
                        .sort((a, b) => compareAirHandlerUnitNames(a.name, b.name))
                        .map(a => (
                          <Select.Item
                            key={a.id}
                            value={a.id}
                            onMouseEnter={() => setItemHovered(a.id)}
                            onMouseLeave={() => setItemHovered(undefined)}
                            disabled={
                              fields.some(f => f.items.includes(a.id)) &&
                              !(fields[currentGroupIndex]?.items.includes(a.id) ?? false)
                            }
                          >
                            {a.name}
                          </Select.Item>
                        ))}
                    </Select.Viewport>
                  </Select.Content>
                </Select.Root>
              )}
            </div>
          )}
        </div>
        <GroupPlanEditor
          floors={floors}
          rooms={rooms}
          airHandlerUnits={airHandlerUnits}
          itemFields={fields}
          selectedItems={currentGroupIndex !== null && fields[currentGroupIndex] ? fields[currentGroupIndex].items : []}
          onChange={(value: string[]) => handleChangeItems(value)}
          itemHovered={itemHovered}
          disabled={currentGroupIndex === null}
          currentGroupIndex={currentGroupIndex}
        />
        <ActionButton className="m-0 col-span-2 mx-auto w-fit" type="submit" disabled={!isDirty}>
          {m.save()}
          <ArrowRightIcon />
        </ActionButton>
      </form>
      {status === "blocked" && <ModificationDialog onClickContinue={() => proceed} open onClose={() => resetBlocker} />}
    </>
  );
};

Groups.displayName = NAME;
